// Phase 6.9 C2 — presentation helpers for the «Лента событий».
//
// The backend ships raw `EventFeedCard`s (kind + raw message). The
// Лента renders an operator-friendly Russian label per kind plus a
// short detail line parsed out of the raw message.

import type { EventFeedCard } from '../api/types';

const KIND_LABELS: Record<string, string> = {
  ppp_online_down: 'PPP-сервис недоступен',
  ppp_online_up: 'PPP-сервис восстановлен',
  watchdog_scan: 'Watchdog: осиротевшие задачи',
  // Reliability Step 3 C2 — outage detector. The raw message is English
  // machinery prose, so feedDetail suppresses it; the label + severity
  // carry the meaning.
  outage: 'Сервис не отвечает — подача на паузе',
  recovered: 'Сервис восстановлен — подача возобновлена',
  // Reliability Step 3 C3 — adaptive watchdog retuned a threshold >20%.
  // The raw message carries the useful «cfg: Xm → Ym» numbers, so
  // feedDetail surfaces it verbatim.
  threshold_shift: 'Порог watchdog изменился',
  scheduler_started: 'Scheduler запущен',
  scheduler_stopped: 'Scheduler остановлен',
  batch_started: 'Серия запущена',
  batch_completed: 'Серия завершена',
  station_substituted: 'Авто-замена станции',
  plate_cascade: 'Каскадная отмена',
  auto_retry_started: 'Авто-ретрай модели',
  task_substituted: 'Замена задачи',
  outlier_result: 'Выброс результата',
  error_burst: 'Серия одинаковых ошибок',
  batch_checkpoint: 'Контрольная точка серии',
};

/** Operator-facing label for a feed card kind. Falls back to the raw
 *  kind for any type not yet in the table. */
export function feedKindLabel(kind: string): string {
  return KIND_LABELS[kind] ?? kind;
}

/** Parse a `k1=v1,k2=v2` extra block out of a notification message. */
function parseExtra(extra: string): Record<string, string> {
  const out: Record<string, string> = {};
  for (const pair of extra.split(',')) {
    const eq = pair.indexOf('=');
    if (eq > 0) out[pair.slice(0, eq).trim()] = pair.slice(eq + 1).trim();
  }
  return out;
}

/** Short human detail line for a feed card, parsed from the raw
 *  message. Returns '' when there is nothing meaningful to add
 *  beyond the kind label. */
export function feedDetail(card: EventFeedCard): string {
  const msg = card.message ?? '';
  const extra = msg.includes('|') ? (msg.split('|', 2)[1] ?? '') : '';
  switch (card.kind) {
    case 'batch_started':
    case 'batch_completed':
    case 'batch_checkpoint':
      return extra ? `«${extra}»` : '';
    case 'station_substituted': {
      const kv = parseExtra(extra);
      return kv.orig_code && kv.new_code
        ? `${kv.orig_code} → ${kv.new_code}`
        : '';
    }
    case 'scheduler_started':
    case 'scheduler_stopped':
      return '';
    // outage/recovered carry English machinery prose in `message`; the
    // Russian label is self-sufficient, so don't surface the raw text.
    case 'outage':
    case 'recovered':
      return '';
    // ppp_online_*, watchdog_scan and any unknown kind already carry a
    // human-readable message — surface it verbatim.
    default:
      return msg;
  }
}

/** Wall-clock HH:MM in the device timezone. The wire timestamp is
 *  UTC (`+00:00` suffix); `Date` converts it to local on render. */
export function feedTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('ru-RU', {
    hour: '2-digit',
    minute: '2-digit',
  });
}
